import { useState } from 'react'
import type { EarningsReport } from '../api/types'
import { Amt, Card, Field, Note, PageHead, TotalBox } from '../components/ui'
import { useApi } from '../hooks/useApi'
import { monthStart, today } from '../lib/format'

/**
 * What the shop kept.
 *
 * The figure at the top is commission plus carriage, less expenses. Everything
 * below it is context and is labelled as such, because this is a commission
 * business: the flowers belong to the grower and the money they fetch only
 * passes through the drawer on its way out again. A month where ten lakh of
 * flowers moved through the shop is not a ten lakh month, and a report that
 * leaves that ambiguous is worse than no report.
 */
export function Earnings() {
  const [from, setFrom] = useState(monthStart())
  const [to, setTo] = useState(today())
  const earnings = useApi<EarningsReport>(`/reports/earnings?from=${from}&to=${to}`, [from, to])

  const data = earnings.data

  return (
    <>
      <PageHead title="مہینے کا حساب" hint="کمیشن + کرایہ − خرچہ = دکان کی بچت۔" />

      <Card>
        <div className="row">
          <Field label="اس تاریخ سے">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="اس تاریخ تک">
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
      </Card>

      {earnings.error && <Note kind="error">{earnings.error}</Note>}

      {data && (
        <>
          {data.provisional && (
            <Note kind="warn">
              اِس عرصے کی <span className="amt">{data.unpricedSupplies}</span> سپلائی کا ریٹ ابھی طے
              نہیں ہوا، اِس لیے یہ حساب ابھی کچا ہے۔ اُن کا ریٹ لگنے کے بعد کمیشن بڑھے گا۔
            </Note>
          )}

          <div className="totals">
            <TotalBox label="کمیشن" value={<Amt value={data.commission} />} />
            <TotalBox label="کرایہ" value={<Amt value={data.kiraya} />} />
            <TotalBox label="خرچہ" value={<Amt value={data.expenses} />} />
            <TotalBox label="دکان کی بچت" value={<Amt value={data.kept} />} accent />
          </div>

          <Card title="یہ آمدنی نہیں ہے">
            <Note kind="info">
              نیچے والے ہندسے صرف دیکھنے کے لیے ہیں۔ مال کے پیسے زمیندار کے ہیں — وہ گلے سے ہو کر
              نکل جاتے ہیں۔ دکان کی اصل کمائی اوپر والی <strong>دکان کی بچت</strong> ہے۔
            </Note>

            <div className="table-wrap">
              <table>
                <tbody>
                  <tr>
                    <td>سارے مال کی رقم</td>
                    <td className="num">
                      <Amt value={data.goodsValue} tone="muted" />
                    </td>
                  </tr>
                  <tr>
                    <td>نقد بکری</td>
                    <td className="num">
                      <Amt value={data.cashSales} tone="muted" />
                    </td>
                  </tr>
                  <tr>
                    <td>اُدھار بکری</td>
                    <td className="num">
                      <Amt value={data.creditSales} tone="muted" />
                    </td>
                  </tr>
                  <tr>
                    <td>پرانی وصولی</td>
                    <td className="num">
                      <Amt value={data.received} tone="muted" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </>
  )
}
